import React from "react"

const ENV_LABEL = { prod: "Production", stage: "Staging", dev: "Development" }
const DAY_MS = 24 * 60 * 60 * 1000
const HOUR_MS = 60 * 60 * 1000

const statusBadge = (uptime) => {
  if (uptime === null || uptime === undefined) return { label: "No data", color: "bg-neutral-200 text-neutral-700" }
  if (uptime >= 0.999) return { label: "Operational", color: "bg-green-100 text-green-700" }
  if (uptime >= 0.8) return { label: "Degraded", color: "bg-amber-100 text-amber-700" }
  return { label: "Down", color: "bg-red-100 text-red-700" }
}

const barColor = (v) => {
  if (v == null) return "bg-canvas border border-bordered"
  if (v >= 0.999) return "bg-green-500"
  if (v >= 0.8) return "bg-amber-500"
  return "bg-red-500"
}

const formatDowntime = (v, stepMs) => {
  if (v == null) return "no data"
  if (v >= 0.999) return "No Downtime"
  const downMs = Math.max(0, (1 - v) * stepMs)
  const totalMin = Math.round(downMs / 60_000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h > 0 && m > 0) return `${h}h ${m} min downtime`
  if (h > 0) return `${h}h downtime`
  return `${m} min downtime`
}

const formatDate = (ts) => {
  const d = new Date(ts)
  const sameYear = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleDateString("en-US", sameYear
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" })
}
const formatHour = (ts) => {
  const d = new Date(ts)
  const sameYear = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleString("en-US", sameYear
    ? { month: "short", day: "numeric", hour: "2-digit" }
    : { month: "short", day: "numeric", year: "numeric", hour: "2-digit" })
}

function padBucketsHour(buckets, from, to) {
  if (!from || !to) return buckets || []
  const fromT = new Date(from).getTime()
  const toT = new Date(to).getTime()
  // Align to KPI hour boundary; ensure last slot = current hour, total span fits range.
  const endHour = Math.floor(toT / HOUR_MS) * HOUR_MS
  const startHour = Math.floor(fromT / HOUR_MS) * HOUR_MS
  const count = Math.max(1, Math.round((endHour - startHour) / HOUR_MS) + 1)
  const slots = []
  for (let i = count - 1; i >= 0; i--) slots.push(endHour - i * HOUR_MS)
  return slots.map((slotTs) => {
    const match = (buckets || []).find((b) => {
      const bt = Math.floor(new Date(b.timestamp).getTime() / HOUR_MS) * HOUR_MS
      return bt === slotTs
    })
    return match || { timestamp: new Date(slotTs).toISOString(), value: null }
  })
}

function padBucketsDay(buckets, from, to) {
  if (!from || !to) return buckets || []
  const fromD = new Date(from)
  const toD = new Date(to)
  const slots = []
  const cursor = new Date(fromD.getFullYear(), fromD.getMonth(), fromD.getDate())
  const last = new Date(toD.getFullYear(), toD.getMonth(), toD.getDate())
  while (cursor <= last) {
    slots.push(new Date(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return slots.map((d) => {
    const match = (buckets || []).find((b) => {
      const bt = new Date(b.timestamp)
      return bt.getFullYear() === d.getFullYear() && bt.getMonth() === d.getMonth() && bt.getDate() === d.getDate()
    })
    return match || { timestamp: d.toISOString(), value: null }
  })
}

function padBuckets(buckets, from, to, granularity) {
  return granularity === "hour" ? padBucketsHour(buckets, from, to) : padBucketsDay(buckets, from, to)
}

const ComponentCard = ({ component, onOpen }) => {
  const uptime = component.uptime ?? null
  const latency = component.avgLatencyMs ?? null
  const checksCount = component.checksCount ?? 0
  const granularity = component.historyGranularity || "day"
  const stepMs = granularity === "hour" ? HOUR_MS : DAY_MS
  const history = padBuckets(component.historyBuckets, component.historyFrom, component.historyTo, granularity)
  // Status badge follows the most-recent non-null bucket — current state,
  // not the window average (which is shown as the "% uptime" label below).
  const currentUptime = (() => {
    for (let i = history.length - 1; i >= 0; i--) {
      const v = history[i]?.value
      if (typeof v === "number") return v
    }
    return uptime
  })()
  const badge = statusBadge(currentUptime)
  const labelFmt = granularity === "hour" ? formatHour : formatDate
  const rangeLabel = (() => {
    if (!component.historyFrom || !component.historyTo) return { left: "", right: "" }
    if (granularity === "hour") return { left: "24h ago", right: "now" }
    const days = Math.round((new Date(component.historyTo) - new Date(component.historyFrom)) / DAY_MS)
    return { left: `${days} days ago`, right: "today" }
  })()

  return (
    <div className="p-4 rounded-lg border border-bordered bg-paper">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {component.currentStatus && component.currentStatus !== "unknown" && (
              <span
                className="relative flex h-2.5 w-2.5 shrink-0"
                title={component.currentStatus === "up" ? "Live: all checks passing" : "Live: check failing"}
              >
                <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${component.currentStatus === "up" ? "bg-green-500" : "bg-red-500"}`} />
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${component.currentStatus === "up" ? "bg-green-500" : "bg-red-500"}`} />
              </span>
            )}
            <button
              type="button"
              onClick={onOpen}
              className="font-medium truncate text-left hover:text-link transition-colors"
            >
              {component.name}
            </button>
          </div>
          <div className="text-xs text-text_secondary mt-1">
            {ENV_LABEL[component.environment] || component.environment}
          </div>
        </div>
        <span className={`shrink-0 text-xs px-2 py-0.5 rounded font-medium ${badge.color}`}>{badge.label}</span>
      </div>

      <div className="mt-3">
        {history.length > 90 ? (
          <div
            className="grid gap-[2px]"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(10px, 1fr))" }}
          >
            {history.map((b, i) => (
              <div
                key={i}
                className={`aspect-square rounded-sm ${barColor(b?.value)}`}
                title={`${labelFmt(b?.timestamp)} — ${formatDowntime(b?.value, stepMs)}`}
              />
            ))}
          </div>
        ) : (
          <div className="flex items-stretch gap-px" style={{ height: 28 }}>
            {history.map((b, i) => (
              <div
                key={i}
                className={`flex-1 rounded-sm min-w-[2px] ${barColor(b?.value)}`}
                title={`${labelFmt(b?.timestamp)} — ${formatDowntime(b?.value, stepMs)}`}
              />
            ))}
          </div>
        )}
        <div className="flex items-center gap-3 text-xs text-text_secondary mt-2">
          <span className="shrink-0">{rangeLabel.left}</span>
          <span className="flex-1 border-t border-bordered" />
          <span className="shrink-0">
            {uptime != null ? `${(uptime * 100).toFixed(2)} % uptime` : "no data"}
            {latency != null && ` · avg ${Math.round(latency)}ms`}
          </span>
          <span className="flex-1 border-t border-bordered" />
          <span className="shrink-0">{rangeLabel.right}</span>
        </div>
      </div>
    </div>
  )
}

export default ComponentCard
